import { compact, sortBy, uniq, uniqBy } from "lodash-es";

export type InvoiceLanguage = "en" | "et" | "lv" | "lt" | "fi";

/** The languages the backend has fee invoice translations for. */
export const INVOICE_LANGUAGE_OPTIONS: {
  value: InvoiceLanguage;
  label: string;
}[] = [
  { value: "en", label: "English" },
  { value: "et", label: "Estonian" },
  { value: "lv", label: "Latvian" },
  { value: "lt", label: "Lithuanian" },
  { value: "fi", label: "Finnish" },
];

export const ROW_GROUP_DESCRIPTION_MAX_LENGTH = 255;

// A "label: count" form sidesteps numeral agreement (e.g. Lithuanian užsakymai/užsakymų).
const ORDERS_LABEL: Record<InvoiceLanguage, string> = {
  en: "orders",
  et: "tellimused",
  lv: "pasūtījumi",
  lt: "užsakymai",
  fi: "tilaukset",
};

// The suffix create_cancellation_fee_items appends to every cancellation fee description.
const ORDER_SUFFIX = /, order #\d+ \((\d{4}-\d{2}-\d{2})\), \d+ MWh$/;

export interface GroupableItem {
  id: number;
  description: string;
  unit_price: number;
  vat_fraction: number | null;
}

/**
 * A merged row of the fee invoice. A null customDescription follows the suggested description,
 * so it tracks the invoice language until the admin types their own.
 */
export interface RowGroupDraft {
  itemIds: number[];
  customDescription: string | null;
}

/** A merged row prints one unit price and VAT rate, so its items must share both. */
export const canMerge = (items: GroupableItem[]): boolean =>
  items.length >= 2 &&
  uniqBy(items, (item) => `${item.unit_price}|${item.vat_fraction ?? 0}`)
    .length === 1;

/**
 * Suggested description for a merged row: the lowest-id item's description without its
 * per-order suffix, plus an order count and date range when the items are cancellation orders.
 */
export const defaultGroupDescription = (
  items: GroupableItem[],
  language: InvoiceLanguage
): string => {
  const [first] = sortBy(items, "id");
  const base = first.description.replace(ORDER_SUFFIX, "");
  const orderDates = sortBy(
    compact(items.map((item) => ORDER_SUFFIX.exec(item.description)?.[1]))
  );
  if (orderDates.length === 0) {
    return base;
  }
  const dateRange = uniq([
    orderDates[0],
    orderDates[orderDates.length - 1],
  ]).join(" – ");
  return `${base}, ${ORDERS_LABEL[language]}: ${orderDates.length} (${dateRange})`;
};

/** The description a group prints under: the admin's own text, else the suggestion. */
export const groupDescription = (
  group: RowGroupDraft,
  members: GroupableItem[],
  language: InvoiceLanguage
): string =>
  group.customDescription ?? defaultGroupDescription(members, language);

/** The reason a printed group description can't be submitted, or null when it is fine. */
export const groupDescriptionError = (description: string): string | null => {
  const length = description.trim().length;
  if (length === 0) {
    return "Description is required.";
  }
  if (length > ROW_GROUP_DESCRIPTION_MAX_LENGTH) {
    return `Description must be ${ROW_GROUP_DESCRIPTION_MAX_LENGTH} characters or fewer (currently ${length}).`;
  }
  return null;
};
